import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Plus,
  Search,
  Edit3,
  Trash2,
  Loader2,
  AlertCircle,
  Building2,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';
import { 
  adminApi,
  type PharmacyListItem,
  type PharmacyCreateRequest,
  type PharmacyUpdateRequest,
  PHARMACY_NAMES
} from '@/services/adminService';

const PharmacyManager = () => {
  const [pharmacies, setPharmacies] = useState<PharmacyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPharmacy, setEditingPharmacy] = useState<PharmacyListItem | null>(null);
  
  // Form states
  const [formData, setFormData] = useState<PharmacyCreateRequest>({
    name: '',
    address: {
      street: [''],
      city: '',
      state: '',
      postal_code: '',
      country: 'United States'
    },
    phone: '',
    email: ''
  });
  
  const [submitting, setSubmitting] = useState(false);

  // Load pharmacies
  const loadPharmacies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {};
      if (searchTerm) params.name = [searchTerm];
      
      const response = await adminApi.getPharmacies(params);
      setPharmacies(response.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pharmacies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPharmacies();
  }, [searchTerm]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.address.city || !formData.address.state) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      if (editingPharmacy) {
        await adminApi.updatePharmacy({
          id: editingPharmacy.id,
          ...formData
        });
      } else {
        await adminApi.createPharmacy(formData);
      }
      
      // Reset form and close dialogs
      setFormData({
        name: '',
        address: {
          street: [''],
          city: '',
          state: '',
          postal_code: '',
          country: 'United States'
        },
        phone: '',
        email: ''
      });
      setShowCreateDialog(false);
      setShowEditDialog(false);
      setEditingPharmacy(null);
      
      // Reload data
      await loadPharmacies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save pharmacy');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    try {
      setError(null);
      await adminApi.deletePharmacy(id);
      await loadPharmacies();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete pharmacy');
    }
  };

  // Handle edit
  const handleEdit = (pharmacy: PharmacyListItem) => {
    setEditingPharmacy(pharmacy);
    setFormData({
      name: pharmacy.name,
      address: {
        street: pharmacy.address.street,
        city: pharmacy.address.city,
        state: pharmacy.address.state,
        postal_code: pharmacy.address.postal_code,
        country: pharmacy.address.country
      },
      phone: pharmacy.phone || '',
      email: pharmacy.email || ''
    });
    setShowEditDialog(true);
  };

  const PharmacyDialog = ({ isEdit = false }: { isEdit?: boolean }) => (
    <Dialog open={isEdit ? showEditDialog : showCreateDialog} onOpenChange={isEdit ? setShowEditDialog : setShowCreateDialog}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-syne font-medium text-vitl-navy">
            {isEdit ? 'Edit Pharmacy' : 'Add New Pharmacy'}
          </DialogTitle>
          <DialogDescription className="font-lato text-vitl-dark/70">
            {isEdit ? 'Update pharmacy details' : 'Create a new pharmacy'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-syne font-medium text-vitl-navy">Basic Information</h3>
            
            <div>
              <label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Pharmacy Name *
              </label>
              <Select 
                value={formData.name} 
                onValueChange={(value) => setFormData({ ...formData, name: value })}
              >
                <SelectTrigger className="font-lato">
                  <SelectValue placeholder="Select pharmacy name" />
                </SelectTrigger>
                <SelectContent>
                  {PHARMACY_NAMES.map((pharmacy) => (
                    <SelectItem key={pharmacy.value} value={pharmacy.value}>
                      {pharmacy.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                  Phone
                </label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="font-lato"
                />
              </div>

              <div>
                <label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                  Email
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="orders@pharmacy.com"
                  className="font-lato"
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-syne font-medium text-vitl-navy">Address</h3>
            
            <div>
              <label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Street Address
              </label>
              <Input
                value={formData.address.street[0]}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  address: { 
                    ...formData.address, 
                    street: [e.target.value] 
                  } 
                })}
                placeholder="123 Main Street"
                className="font-lato"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                  City *
                </label>
                <Input
                  value={formData.address.city}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    address: { 
                      ...formData.address, 
                      city: e.target.value 
                    } 
                  })}
                  placeholder="City"
                  className="font-lato"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                  State *
                </label>
                <Input
                  value={formData.address.state}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    address: { 
                      ...formData.address, 
                      state: e.target.value 
                    } 
                  })}
                  placeholder="State"
                  className="font-lato"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                  Postal Code
                </label>
                <Input
                  value={formData.address.postal_code}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    address: { 
                      ...formData.address, 
                      postal_code: e.target.value 
                    } 
                  })}
                  placeholder="12345"
                  className="font-lato"
                />
              </div>

              <div>
                <label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                  Country
                </label>
                <Input
                  value={formData.address.country}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    address: { 
                      ...formData.address, 
                      country: e.target.value 
                    } 
                  })}
                  className="font-lato"
                  disabled
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                <p className="text-red-700 font-lato text-sm">{error}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setShowEditDialog(false);
                setEditingPharmacy(null);
                setError(null);
              }}
              className="font-lato"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-lato font-medium"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEdit ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEdit ? 'Update Pharmacy' : 'Create Pharmacy'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-syne font-medium text-vitl-navy mb-2">Pharmacy Management</h2>
          <p className="text-vitl-dark/70 font-lato">Manage pharmacy partners and their contact information</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-lato font-medium">
              <Plus className="w-4 h-4 mr-2" />
              Add Pharmacy
            </Button>
          </DialogTrigger>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="border-l-4 border-l-emerald-500 shadow-sm">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-vitl-dark/50" />
              <Input
                placeholder="Search pharmacies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 font-lato"
              />
            </div>
            
            <div className="flex items-center text-sm text-vitl-dark/70 font-lato">
              <Building2 className="w-4 h-4 mr-2" />
              {pharmacies.length} pharmacies
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
            <p className="text-red-700 font-lato text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      )}

      {/* Pharmacies Table */}
      {!loading && (
        <Card className="border-l-4 border-l-emerald-500 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-syne font-medium text-vitl-navy flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-emerald-600" />
              Pharmacy Partners
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pharmacies.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-vitl-dark/30 mx-auto mb-4" />
                <p className="text-vitl-dark/70 font-lato">No pharmacies found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-lato font-medium">Name</TableHead>
                    <TableHead className="font-lato font-medium">Address</TableHead>
                    <TableHead className="font-lato font-medium">Contact</TableHead>
                    <TableHead className="font-lato font-medium text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pharmacies.map((pharmacy) => (
                    <TableRow key={pharmacy.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium font-lato text-vitl-navy">
                        {pharmacy.name}
                      </TableCell>
                      <TableCell className="font-lato text-vitl-dark/70">
                        <div className="flex items-start">
                          <MapPin className="w-4 h-4 mr-2 mt-0.5 text-vitl-dark/50" />
                          <div>
                            {pharmacy.address.street.length > 0 && (
                              <div>{pharmacy.address.street.join(', ')}</div>
                            )}
                            <div>
                              {pharmacy.address.city}, {pharmacy.address.state} {pharmacy.address.postal_code}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-lato text-vitl-dark/70">
                        <div className="space-y-1">
                          {pharmacy.phone && (
                            <div className="flex items-center">
                              <Phone className="w-3 h-3 mr-2 text-vitl-dark/50" />
                              {pharmacy.phone}
                            </div>
                          )}
                          {pharmacy.email && (
                            <div className="flex items-center">
                              <Mail className="w-3 h-3 mr-2 text-vitl-dark/50" />
                              {pharmacy.email}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(pharmacy)}
                            className="font-lato"
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 font-lato"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="font-syne font-medium text-vitl-navy">
                                  Delete Pharmacy
                                </AlertDialogTitle>
                                <AlertDialogDescription className="font-lato text-vitl-dark/70">
                                  Are you sure you want to delete "{pharmacy.name}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="font-lato">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(pharmacy.id)}
                                  className="bg-red-600 hover:bg-red-700 font-lato"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <PharmacyDialog />
      <PharmacyDialog isEdit />
    </div>
  );
};

export default PharmacyManager;